import React, { useEffect, useMemo, useState } from "react";
import { ActionIcon, Anchor, Group, Modal, MultiSelect, Select, Stack, Stepper, Text, TextInput } from "@mantine/core";
import { Alert, Button, spacePx } from "@xplatform/design-system";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { CataloguePlan, PermissionCatalogue, PlanCreate, PlanKind, Role } from "@models/access";
import { useCataloguePlan, useEscalationGuard } from "@hooks/access";
import PlanStepper from "./PlanStepper";
import { planUsersAffected } from "./plans";

interface CutoverWizardProps {
  opened: boolean;
  kind: PlanKind;
  /** An open plan to resume instead of a new one. */
  resume: CataloguePlan | null;
  catalogue: PermissionCatalogue;
  roles: Role[];
  environment: string;
  onClose: () => void;
  /** A step ran or the plan changed: the catalogue reloads. */
  onChanged: () => void;
}

interface Row {
  moduleName: string;
  key: string;
  name: string;
  /** Rename only: the existing key. */
  from: string;
}

const emptyRow = (): Row => ({ moduleName: "", key: "", name: "", from: "" });
const KEY = /^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9]+)*$/;

/**
 * Add, rename or delete permissions in BeeHive without anyone losing access in
 * between (§7): fill in the keys, preview every step and the roles it changes,
 * then run the steps in order, one per request, with the deploy gate before
 * anything is removed.
 */
const CutoverWizard: React.FC<CutoverWizardProps> = ({
  opened, kind, resume, catalogue, roles, environment, onClose, onChanged,
}) => {
  const { t } = useTranslation();
  const { canEditRole } = useEscalationGuard();
  const { plan, setPlan, busy, error, preview, runStep, cancel, reset } = useCataloguePlan();
  const [rows, setRows] = useState<Row[]>([emptyRow()]);
  const [grantTo, setGrantTo] = useState<string[]>([]);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!opened) return;
    reset();
    setRows([emptyRow()]);
    setGrantTo([]);
    setStarted(false);
    if (resume) {
      setPlan(resume);
      setStarted(true);
    }
  }, [opened, resume, reset, setPlan]);

  const effectiveKind = plan?.kind ?? kind;
  const existing = useMemo(
    () => catalogue.modules.flatMap((m) => m.permissions.map((p) => ({ ...p, moduleName: m.name }))),
    [catalogue],
  );
  const existingKeys = useMemo(() => new Set(existing.map((p) => p.key)), [existing]);
  // BeeHive's modules (created under Access > Modules): never a new name typed here.
  const moduleNames = catalogue.modules.map((m) => m.name).sort((a, b) => a.localeCompare(b));

  const rowError = (row: Row): string | null => {
    if (effectiveKind === "delete") return row.from ? null : "";
    if (effectiveKind === "rename" && !row.from) return "";
    if (!row.key || !row.moduleName || !row.name) return "";
    if (!KEY.test(row.key)) return t("admin.access.permissions.form.keyInvalid");
    if (existingKeys.has(row.key)) return t("admin.access.permissions.form.keyExists");
    return null;
  };
  const invalid = rows.some((r) => rowError(r) !== null);

  const body = (): PlanCreate => {
    if (effectiveKind === "add") {
      return { kind: "add", grantToRoleIds: grantTo,
               permissions: rows.map((r) => ({ moduleName: r.moduleName, key: r.key.trim(), name: r.name.trim() })) };
    }
    if (effectiveKind === "rename") {
      return { kind: "rename", renames: rows.map((r) => ({ from: r.from, to: r.key.trim(), moduleName: r.moduleName,
                                                         name: r.name.trim() })) };
    }
    return { kind: "delete", keys: rows.map((r) => ({
      key: r.from, moduleName: existing.find((p) => p.key === r.from)?.moduleName ?? "",
    })) };
  };

  const update = (i: number, patch: Partial<Row>) =>
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const done = plan?.status === "done";
  const phase = !plan ? 0 : !started ? 1 : done ? 3 : 2;

  const runAndRefresh = async (n: number, gate: boolean) => {
    await runStep(n, gate);
    onChanged();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={t(`admin.access.permissions.wizard.title.${effectiveKind}`)}
      portalProps={{ target: "#admin-portal-root" }}
      closeOnClickOutside={false}
    >
      <Stack gap="lg">
        <Stepper active={phase} size="xs">
          <Stepper.Step label={t("admin.access.permissions.wizard.form")} />
          <Stepper.Step label={t("admin.access.permissions.wizard.preview")} />
          <Stepper.Step label={t("admin.access.permissions.wizard.run")} />
          <Stepper.Step label={t("admin.access.permissions.wizard.done")} />
        </Stepper>

        {phase === 0 && (
          <Stack gap="md">
            {rows.map((row, i) => (
              <Group key={i} align="flex-end" gap="sm" wrap="nowrap">
                {effectiveKind !== "add" && (
                  <Select
                    label={t(effectiveKind === "rename" ? "admin.access.permissions.form.from" : "admin.access.permissions.form.key")}
                    data={existing.map((p) => ({ value: p.key, label: p.key }))}
                    value={row.from || null}
                    searchable
                    flex={1}
                    onChange={(v) => {
                      const source = existing.find((p) => p.key === v);
                      update(i, { from: v ?? "", moduleName: source?.moduleName ?? row.moduleName,
                                  name: row.name || source?.name || "" });
                    }}
                    comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
                  />
                )}
                {effectiveKind !== "delete" && (
                  <>
                    <TextInput
                      label={t(effectiveKind === "rename" ? "admin.access.permissions.form.to" : "admin.access.permissions.form.key")}
                      value={row.key}
                      flex={1}
                      error={rowError(row) || undefined}
                      onChange={(e) => update(i, { key: e.currentTarget.value })}
                    />
                    <TextInput
                      label={t("admin.access.permissions.form.name")}
                      value={row.name}
                      flex={1}
                      onChange={(e) => update(i, { name: e.currentTarget.value })}
                    />
                    <Select
                      label={t("admin.access.permissions.form.module")}
                      description={t("admin.access.permissions.form.moduleHint")}
                      data={moduleNames}
                      value={row.moduleName || null}
                      searchable
                      w={180}
                      onChange={(v) => update(i, { moduleName: v ?? "" })}
                      nothingFoundMessage={t("admin.access.permissions.form.noModule")}
                      comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
                    />
                  </>
                )}
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label={t("admin.access.permissions.form.removeRow")}
                  disabled={rows.length === 1}
                  onClick={() => setRows(rows.filter((_, j) => j !== i))}
                  mb={spacePx[1]}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
            <Group>
              <Button variant="subtle" leftSection={<IconPlus size={16} />} onClick={() => setRows([...rows, emptyRow()])}>
                {t("admin.access.permissions.form.addRow")}
              </Button>
            </Group>
            {effectiveKind === "add" && (
              <MultiSelect
                label={t("admin.access.permissions.form.grantTo")}
                description={t("admin.access.permissions.form.grantToHint")}
                data={roles.map((r) => ({ value: r.id, label: r.name, disabled: !canEditRole(r) }))}
                value={grantTo}
                onChange={setGrantTo}
                comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
              />
            )}
            {error && <Alert color="red">{error.message}</Alert>}
            <Group justify="flex-end">
              <Button variant="subtle" onClick={onClose}>{t("common.cancel")}</Button>
              <Button disabled={invalid} loading={busy} onClick={() => void preview(body())}>
                {t("admin.access.permissions.wizard.toPreview")}
              </Button>
            </Group>
          </Stack>
        )}

        {phase === 1 && plan && (
          <Stack gap="md">
            <Alert color="blue">
              {t("admin.access.permissions.wizard.previewSummary", { count: plan.steps.length, users: planUsersAffected(plan) })}
            </Alert>
            <Stack gap="xs" data-testid="plan-preview">
              {plan.steps.map((step) => (
                <Stack key={step.n} gap={spacePx[1]}>
                  <Text size="sm" fw={600}>
                    {step.n}. {t(`admin.access.permissions.steps.${step.operation}`)}
                  </Text>
                  <Text size="xs" c="dimmed">{step.title}</Text>
                  {step.roles.map((r) => (
                    <Text key={r.id} size="xs">
                      {t("admin.access.permissions.roleImpact", { role: r.name, count: r.assignedUsers })}
                    </Text>
                  ))}
                </Stack>
              ))}
            </Stack>
            <Group justify="flex-end">
              <Button variant="subtle" onClick={async () => { await cancel(); onChanged(); onClose(); }}>
                {t("admin.access.permissions.wizard.discard")}
              </Button>
              <Button onClick={() => setStarted(true)}>{t("admin.access.permissions.wizard.start")}</Button>
            </Group>
          </Stack>
        )}

        {phase === 2 && plan && (
          <Stack gap="md">
            <PlanStepper plan={plan} environment={environment} busy={busy} error={error} onRun={runAndRefresh} />
            <Group justify="space-between">
              <Button variant="subtle" color="red" loading={busy}
                      onClick={async () => { await cancel(); onChanged(); onClose(); }}>
                {t("admin.access.permissions.wizard.cancelPlan")}
              </Button>
              <Button variant="subtle" onClick={onClose}>{t("admin.access.permissions.wizard.later")}</Button>
            </Group>
          </Stack>
        )}

        {phase === 3 && plan && (
          <Stack gap="md">
            <Alert color="green" title={t("admin.access.permissions.wizard.doneTitle")}>
              {t("admin.access.permissions.wizard.doneSummary", { count: plan.steps.length })}
            </Alert>
            <Group justify="space-between">
              <Anchor component={Link} to="/admin/access/audit?action=permissions.plan.step" size="sm">
                {t("admin.access.permissions.wizard.auditLink")}
              </Anchor>
              <Button onClick={onClose}>{t("admin.access.permissions.wizard.close")}</Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};

export default CutoverWizard;
